import React, { useRef, useEffect, useCallback } from 'react';

interface Pulse {
    x: number;
    y: number;
    radius: number;
    maxRadius: number;
    opacity: number;
    speed: number;
    colorType: 'cool' | 'orange'; // New property for color variation
}

/**
 * Pulse effects canvas component.
 * Renders periodic expanding soft circles at random grid intersections.
 * Now features mixed cool (purple) and warm (orange) pulses.
 */
const PulseCanvas: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const pulsesRef = useRef<Pulse[]>([]);
    const rafRef = useRef<number>(0);
    const lastPulseTimeRef = useRef<number>(0);
    const dimensionsRef = useRef({ width: 0, height: 0 });

    const createPulse = useCallback(() => {
        const { width, height } = dimensionsRef.current;
        if (width === 0 || height === 0) return;

        const gridSize = 40;
        const gridX = Math.floor(Math.random() * (width / gridSize)) * gridSize;
        const gridY = Math.floor(Math.random() * (height / gridSize)) * gridSize;

        // 30% chance for orange pulse, 70% chance for cool pulse
        const isOrange = Math.random() < 0.3;

        const pulse: Pulse = {
            x: gridX,
            y: gridY,
            radius: 0,
            maxRadius: 80 + Math.random() * 60,
            opacity: 0.6,
            speed: 0.8 + Math.random() * 0.4,
            colorType: 'orange'
        };

        pulsesRef.current.push(pulse);
    }, []);

    const draw = useCallback((timestamp: number) => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        const width = rect.width;
        const height = rect.height;

        dimensionsRef.current = { width, height };

        // Set canvas size accounting for device pixel ratio
        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.scale(dpr, dpr);

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        // Create new pulse periodically
        if (timestamp - lastPulseTimeRef.current > 2000) {
            createPulse();
            lastPulseTimeRef.current = timestamp;
        }

        // Use 'lighter' blend mode for glowing effect
        ctx.globalCompositeOperation = 'lighter';

        // Update and draw pulses
        const activePulses: Pulse[] = [];

        for (const pulse of pulsesRef.current) {
            // Update pulse
            pulse.radius += pulse.speed;
            pulse.opacity = Math.max(0, 0.6 * (1 - pulse.radius / pulse.maxRadius));

            // Draw pulse if still visible
            if (pulse.opacity > 0.01) {
                const gradient = ctx.createRadialGradient(
                    pulse.x, pulse.y, 0,
                    pulse.x, pulse.y, pulse.radius
                );

                if (pulse.colorType === 'orange') {
                    // Orange/Amber Pulse
                    gradient.addColorStop(0, `rgba(255, 140, 0, 0)`);
                    gradient.addColorStop(0.5, `rgba(255, 140, 0, ${pulse.opacity * 0.4})`); // Orange core
                    gradient.addColorStop(0.7, `rgba(255, 180, 80, ${pulse.opacity * 0.6})`); // Amber mid
                    gradient.addColorStop(1, `rgba(255, 140, 0, 0)`);
                } else {
                    // Cool/Purple Pulse (Existing style)
                    gradient.addColorStop(0, `rgba(139, 92, 246, 0)`);
                    gradient.addColorStop(0.5, `rgba(139, 92, 246, ${pulse.opacity * 0.3})`);
                    gradient.addColorStop(0.7, `rgba(99, 102, 241, ${pulse.opacity * 0.5})`);
                    gradient.addColorStop(1, `rgba(99, 102, 241, 0)`);
                }

                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(pulse.x, pulse.y, pulse.radius, 0, Math.PI * 2);
                ctx.fill();

                activePulses.push(pulse);
            }
        }

        // Reset composite operation
        ctx.globalCompositeOperation = 'source-over';

        pulsesRef.current = activePulses;

        rafRef.current = requestAnimationFrame(draw);
    }, [createPulse]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        // Handle resize
        const resizeObserver = new ResizeObserver(() => {
            const rect = canvas.getBoundingClientRect();
            dimensionsRef.current = { width: rect.width, height: rect.height };
        });

        resizeObserver.observe(canvas);

        // Start animation loop
        rafRef.current = requestAnimationFrame(draw);

        return () => {
            resizeObserver.disconnect();
            cancelAnimationFrame(rafRef.current);
        };
    }, [draw]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ width: '100%', height: '100%', transform: 'translate3d(0,0,0)', backfaceVisibility: 'hidden' }}
            aria-label="Pulse effects layer"
        />
    );
};

export default PulseCanvas;
